import React, { useState, useMemo, useRef, useCallback } from 'react';
import CardDetail from '../CardDetail.js';
import CardPreview from '../CardPreview.js';
import CostCurveChart from '../CostCurveChart.js';
import DeckBuilderLibraryCard from '../DeckBuilderLibraryCard.js';
import { getCardName, getCardLoc, getUILoc } from '../../utils/cardUtils.js';
import { CardIllustration } from '../Card.js';
import '../DeckBuilderScreen.css';
import '../CostCurveChart.css';
import './HypothesisDeckEditorView.css';
import audioManager from '../../utils/AudioManager.js';

const HypothesisDeckEditorView = ({ initialDeckCards, cardDefs, title, onSaveAndBack }) => {
  // デッキはカードオブジェクトの配列（押下順に並ぶ）
  const [deck, setDeck] = useState(() => {
    if (!initialDeckCards || !cardDefs) return [];
    return initialDeckCards.map(cardId => {
      return cardDefs[cardId] || Object.values(cardDefs).find(def => def.name === cardId);
    }).filter(Boolean);
  });

  const [sortBy, setSortBy] = useState('implementation');
  const [filterType, setFilterType] = useState('all');
  const [filterScale, setFilterScale] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCard, setSelectedCard] = useState(null);
  const [costCurveMode, setCostCurveMode] = useState('single');

  // ホバープレビュー
  const [hoveredCard, setHoveredCard] = useState(null);
  const [previewPos, setPreviewPos] = useState({ x: 0, y: 0 });
  const previewRef = useRef(null);

  const handleMouseEnter = useCallback((card, e) => {
    if (hoveredCard?.id === card.id) return;
    const rect = e.currentTarget.getBoundingClientRect();
    setHoveredCard(card);

    const pollSize = () => {
      if (previewRef.current && previewRef.current.offsetHeight > 0) {
        const previewHeight = previewRef.current.offsetHeight;
        const previewWidth = previewRef.current.offsetWidth;
        const x = (rect.right + rect.left) / 2;
        const y = (rect.top + rect.bottom) / 2;
        const isOverflowRight = (x + previewWidth) > window.innerWidth;
        const isOverflowBottom = (y + previewHeight) > window.innerHeight;

        setPreviewPos({
          x: isOverflowRight ? Math.max(10, rect.left - previewWidth - 10) : x,
          y: isOverflowBottom ? Math.max(10, window.innerHeight - previewHeight - 10) : y
        });
      } else {
        requestAnimationFrame(pollSize);
      }
    };
    requestAnimationFrame(pollSize);
  }, [hoveredCard]);

  const handleCardMouseLeave = useCallback(() => {
    setHoveredCard(null);
  }, []);

  const filteredAndSortedCards = useMemo(() => {
    if (!cardDefs) return [];
    let cards = Object.values(cardDefs);
    cards = cards.filter(card => !card.is_token);
    
    // 有効フラグフィルター
    cards = cards.filter(card => card.is_enabled !== false);

    if (filterType !== 'all') {
      const typeNum = parseInt(filterType, 10);
      cards = cards.filter(card => card.card_type === typeNum);
    }

    if (filterScale !== 'all') {
      cards = cards.filter(card => {
        const scale = card.required_scale;
        switch (filterScale) {
          case '0-2': return scale >= 0 && scale <= 2;
          case '3-15': return scale >= 3 && scale <= 15;
          case '16+': return scale >= 16;
          default: return true;
        }
      });
    }

    // テキスト絞り込み（スペース区切りの複数キーワード対応）
    if (searchQuery.trim()) {
      const keywords = searchQuery.trim().toLowerCase().split(/[\s　]+/).filter(Boolean);
      if (keywords.length > 0) {
        cards = cards.filter(card => {
          const cardLoc = getCardLoc(card.id);
          const name = (cardLoc?.name || card.name || '').toLowerCase();
          const desc = (cardLoc?.description || card.description || '').toLowerCase();
          const fullText = `${name} ${desc}`;
          return keywords.every(kw => fullText.includes(kw));
        });
      }
    }

    cards.sort((a, b) => {
      if (sortBy === 'requiredScale') {
        return a.required_scale - b.required_scale;
      }
      return (a.definitionOrder || 0) - (b.definitionOrder || 0);
    });

    return cards;
  }, [cardDefs, sortBy, filterType, filterScale, searchQuery]);

  const costCurveData = useMemo(() => {
    const counts = {};
    if (costCurveMode === 'single') {
      deck.forEach(card => {
        const cost = card.required_scale;
        counts[cost] = (counts[cost] || 0) + 1;
      });
      const maxCost = Math.max(...Object.keys(counts).map(Number), 0);
      const data = [];
      for (let i = 0; i <= maxCost; i++) {
        if (counts[i] > 0) {
          data.push({ label: `${i}`, count: counts[i] });
        }
      }
      return data;
    } else {
      const groups = { '0': 0, '5': 0, '10': 0, '15': 0, '20+': 0 };
      deck.forEach(card => {
        const cost = card.required_scale;
        if (cost <= 4) groups['0']++;
        else if (cost <= 9) groups['5']++;
        else if (cost <= 14) groups['10']++;
        else if (cost <= 19) groups['15']++;
        else groups['20+']++;
      });
      return Object.entries(groups).map(([label, count]) => ({ label, count }));
    }
  }, [deck, costCurveMode]);

  // 【仮説検証仕様】カードを末尾に追加（ソートなし）
  const addCardToDeck = useCallback((card) => {
    audioManager.playSE('select');
    setDeck(prevDeck => [...prevDeck, card]);
  }, []);

  // 【仮説検証仕様】クリックしたインデックスのカードを削除
  const removeCardFromDeckAtIndex = useCallback((index) => {
    audioManager.playSE('cancel');
    setDeck(prevDeck => prevDeck.filter((_, i) => i !== index));
  }, []);

  const handleShowCardDetails = useCallback((card) => {
    audioManager.playSE('select');
    setSelectedCard(card);
  }, []);

  const handleExit = () => {
    audioManager.playSE('select');
    const finalCardIds = deck.map(c => c.id);
    onSaveAndBack(finalCardIds);
  };

  return (
    <div className="deck-builder-screen hypothesis-deck-editor-view" style={{ zIndex: 100, position: 'absolute', top: 0, left: 0, width: '100%', height: '100%' }}>
      {selectedCard && (
        <CardDetail card={selectedCard} onClose={() => setSelectedCard(null)} />
      )}

      <div className="deck-builder-container">
        <div className="deck-builder-header">
          <div className="header-left">
            <h1 className="hypothesis-editor-title">{title || getUILoc('BUTTONS.EDIT_DECK')}</h1>
          </div>
          <button onClick={handleExit} className="back-button">
            ← {getUILoc('BUTTONS.BACK')}
          </button>
        </div>

        <div className="deck-builder-layout">
          {/* 左側: カードプール */}
          <div className="card-library-section">
            <div className="library-header">
              <div className="library-title-row">
                <h2>{getUILoc('LABELS.CARDS')}</h2>
                <div className="card-search-container">
                  <input
                    type="text"
                    className="card-search-input"
                    placeholder={getUILoc('LABELS.SEARCH_CARDS')}
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                  />
                  {searchQuery && (
                    <button
                      type="button"
                      className="card-search-clear-btn"
                      onClick={() => setSearchQuery('')}
                      aria-label="Clear search"
                    >
                      ×
                    </button>
                  )}
                </div>
              </div>
              <div className="library-controls">
                <div className="sort-controls">
                  <label>{getUILoc('LABELS.SORT')}:</label>
                  <select value={sortBy} onChange={(e) => { audioManager.playSE('select'); setSortBy(e.target.value); }}>
                    <option value="implementation">{getUILoc('LABELS.SORT_IMPLEMENTATION')}</option>
                    <option value="requiredScale">{getUILoc('LABELS.SORT_SCALE')}</option>
                  </select>
                </div>
                <div className="filter-controls">
                  <label>{getUILoc('LABELS.CARD_TYPE')}:</label>
                  <select value={filterType} onChange={(e) => { audioManager.playSE('select'); setFilterType(e.target.value); }}>
                    <option value="all">{getUILoc('LABELS.FILTER_ALL_TYPE')}</option>
                    <option value="1">{getUILoc('CARD_TYPES.1')}</option>
                    <option value="2">{getUILoc('CARD_TYPES.2')}</option>
                    <option value="3">{getUILoc('CARD_TYPES.3')}</option>
                  </select>

                  <label>{getUILoc('LABELS.REQUIRED_SCALE')}:</label>
                  <select value={filterScale} onChange={(e) => { audioManager.playSE('select'); setFilterScale(e.target.value); }}>
                    <option value="all">{getUILoc('LABELS.FILTER_ALL_SCALE')}</option>
                    <option value="0-2">0-2</option>
                    <option value="3-15">3-15</option>
                    <option value="16+">16+</option>
                  </select>
                </div>
              </div>
            </div>

            <div id="card-library-grid" className="deck-builder-card-grid">
              {filteredAndSortedCards.map((card) => (
                <div 
                  key={card.id} 
                  className="library-card-hover-zone"
                  onMouseEnter={(e) => handleMouseEnter(card, e)}
                  onMouseLeave={handleCardMouseLeave}
                  style={{ position: 'relative' }}
                >
                  <DeckBuilderLibraryCard
                    card={card}
                    onTap={() => addCardToDeck(card)}
                    onSwipeLeft={() => handleShowCardDetails(card)}
                  />
                  {hoveredCard?.id === card.id && (
                    <div 
                      ref={previewRef}
                      className="preview-tooltip-container"
                      style={{ 
                        position: 'fixed', 
                        left: previewPos.x,
                        top: previewPos.y,
                        zIndex: 2000,
                        pointerEvents: 'auto'
                      }}
                    >
                      <div onWheel={(e) => e.stopPropagation()}>
                        <CardPreview card={hoveredCard} cardDefs={Object.values(cardDefs)} />
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* 右側: デッキ編集エリア（押下順・非集約・クリックで個別削除） */}
          <div className="deck-building-section">
            <h2 id="deck-size-info">
              {getUILoc('LABELS.CURRENT_DECK')} ({getUILoc('UNITS.CARDS', { count: deck.length })})
            </h2>
            <div className="deck-list" style={{ minHeight: '180px', maxHeight: '420px', overflowY: 'auto' }}>
              {deck.length === 0 ? (
                <div id="empty-deck-placeholder" className="empty-deck-placeholder">
                  <p dangerouslySetInnerHTML={{ __html: getUILoc('MESSAGES.ADD_CARD_FROM_POOL') }}></p>
                </div>
              ) : (
                deck.map((card, index) => (
                  <div 
                    key={`${card.id}-${index}`} 
                    className="deck-card"
                    onClick={() => removeCardFromDeckAtIndex(index)}
                    title={getUILoc('LABELS.REMOVE_CARD_HINT')}
                    style={{ cursor: 'pointer' }}
                  >
                    <CardIllustration cardId={card.id} isBackground={true} />
                    <span className="card-name-label" style={{ fontSize: '0.85rem' }}>
                      {index + 1}. {getCardName(card.id)} ({card.required_scale})
                    </span>
                  </div>
                ))
              )}
            </div>

            <div className="cost-curve-wrapper">
              <div className="cost-curve-header">
                <h3>{getUILoc('LABELS.SCALE_DISTRIBUTION')}</h3>
                <button onClick={() => { audioManager.playSE('select'); setCostCurveMode(prev => prev === 'single' ? 'grouped' : 'single'); }}>
                  {getUILoc('LABELS.SWITCH_DISPLAY')} ({costCurveMode === 'single' ? getUILoc('LABELS.STEP_1') : getUILoc('LABELS.STEP_5')})
                </button>
              </div>
              <CostCurveChart data={costCurveData} />
            </div>

            <div className="deck-actions hypothesis-deck-actions">
              <button 
                onClick={handleExit} 
                className="hypothesis-save-back-btn"
              >
                ← {getUILoc('BUTTONS.BACK')}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HypothesisDeckEditorView;
